import { Injectable, inject } from '@angular/core';
import { CollectionRequestBuilder } from '@web/common';
import { Guid, isNil, Nillable } from '@web/utils';
import { catchError, map, Observable, of } from 'rxjs';
import { SelectItem } from '@web/shared/ui';
import { PublicDictionaryCountryApiService } from '../api/services/public-dictionary-country-api.service';
import { PublicDictionaryCountryCollectionItemResponse } from '../api/responses/public-dictionary-country-collection-item.response';

@Injectable({
  providedIn: 'root',
})
export class PublicDictionaryCountrySelectItemsProviderService {
  /* prettier-ignore */
  private readonly dictionaryCountryApi = inject(PublicDictionaryCountryApiService);
  private readonly collectionRequestBuilder = inject(CollectionRequestBuilder);

  /* prettier-ignore */
  public fetchSelectItems<Key extends keyof PublicDictionaryCountryCollectionItemResponse>(key: Key, query: Nillable<string> = null): Observable<SelectItem<PublicDictionaryCountryCollectionItemResponse[Key]>[]> {
    return this.fetchCollection()
      .pipe(
        map((items) =>
          items
            .filter(item => {
              if (isNil(query)) {
                return item;
              }

              return item.label.toLowerCase().includes(query);
            })
            .map((item) => new SelectItem(item.label, item[key])),
        ),
        catchError(() => of([])),
      );
  }

  public fetchIsoCodeForId(id: Guid): Observable<string> {
    return this.fetchCollection().pipe(
      map((items) => {
        const map = new Map(items.map((value) => [value.id, value.isoCode]));

        return map.get(id) as string;
      }),
    );
  }

  private fetchCollection(): Observable<
    PublicDictionaryCountryCollectionItemResponse[]
  > {
    return this.dictionaryCountryApi
      .fetchCollection(
        this.collectionRequestBuilder
          .sorts((builder) => [builder.ascendingFor('label').build()])
          .disablePagination()
          .build(),
        true,
      )
      .pipe(
        map(({ items }) => items),
        catchError(() => of([])),
      );
  }
}
